import type { SketchGroup } from '../../../mesh/sketch/SketchGroup';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import {
	REFERENCE_TARGET_REBUILT_COMMIT_REASON,
	type SketchEntityReferenceTargetChange,
} from '../model/SketchEntityReferencePropagation';
import type {
	SketchEntityReferenceTargetChangeCommitterRuntime,
	CommitSketchEntityReferenceTargetChangesInput,
	CommitSketchEntityReferenceTargetChangesResult,
	PreparedSketchEntityReferenceTargetChanges,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceTargetChangeCommitter';

export type {
	SketchEntityReferenceTargetChangeCommitterRuntime,
	CommitSketchEntityReferenceTargetChangesInput,
	CommitSketchEntityReferenceTargetChangesResult,
	PreparedSketchEntityReferenceTargetChanges,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceTargetChangeCommitter';

/**
 * 引用目标变化提交器。
 *
 * 设计说明：
 * 1. `RebuildSketchEntityReferencesService` 只负责重建实体和记录，不直接持有事件聚合细节；
 * 2. 这里按 target sketch 聚合变化，保证同一草图在一个 rebuild 批次内只发布一次提交事件；
 * 3. 若目标草图已经存在未提交事务，拒绝发布引用提交，避免把用户事务误标记为 `reference-target-rebuilt`。
 */
export class SketchEntityReferenceTargetChangeCommitter {
	/**
	 * 提交引用目标重建产生的草图定义事务。
	 *
	 * @param input 提交输入。
	 * @returns 提交结果。
	 */
	public commit(input: CommitSketchEntityReferenceTargetChangesInput): CommitSketchEntityReferenceTargetChangesResult {
		const preparation = this.prepare(input);
		const commitResult = this.commitPrepared(preparation);
		return {
			committedSketchIds: commitResult.committedSketchIds,
			diagnostics: [...preparation.diagnostics, ...commitResult.diagnostics],
		};
	}

	/**
	 * 在引用仓储正式提交前完成全部可失败校验。
	 *
	 * @param input 提交输入。
	 * @returns 不修改草图定义的确定性发布计划。
	 */
	public prepare(
		input: CommitSketchEntityReferenceTargetChangesInput,
	): PreparedSketchEntityReferenceTargetChanges {
		const isInputCurrent = input.isInputCurrent ?? (() => true);
		if (input.changes.length <= 0 || !isInputCurrent()) {
			return {
				sketchCommits: [],
				preparedSketchIds: [],
				rejectedSketchIds: [],
				diagnostics: [],
			};
		}

		const sketchCommits: PreparedSketchEntityReferenceTargetChanges['sketchCommits'][number][] = [];
		const preparedSketchIds: string[] = [];
		const rejectedSketchIds: string[] = [];
		const diagnostics: ExecutionDiagnostic[] = [];
		const propagationId = input.propagationId ?? this.createPropagationId();
		const batchReferenceIds = this.collectUnique(input.changes.map((change) => change.referenceId));
		this.groupChangesByTargetSketch(input.changes).forEach((changes, targetSketchId) => {
			if (!isInputCurrent()) {
				rejectedSketchIds.push(targetSketchId);
				return;
			}
			const targetSketch = input.runtime.getSketchGroup(targetSketchId);
			if (!targetSketch) {
				rejectedSketchIds.push(targetSketchId);
				const firstChange = changes[0];
				diagnostics.push(createExecutionDiagnostic({
					kind: 'rebuild-failed',
					level: 'warning',
					message: `引用目标重建提交失败：目标草图不存在 ${targetSketchId}，关联引用: ${
						changes.map((change) => change.referenceId).join(', ')
					}`,
					referenceId: firstChange?.referenceId,
					targetEntityId: firstChange?.newTargetEntityId,
				}));
				return;
			}

			const baselineRevision = input.targetSketchBaselineDefinitionRevisions?.get(targetSketchId);
			if (
				input.allowedDefinitionChangeOwnerId
				&& baselineRevision !== undefined
				&& targetSketch.hasDefinitionChangesOutsideOwnerSince(
					baselineRevision,
					input.allowedDefinitionChangeOwnerId,
				)
			) {
				rejectedSketchIds.push(targetSketchId);
				const firstChange = changes[0];
				diagnostics.push(createExecutionDiagnostic({
					kind: 'rebuild-failed',
					level: 'warning',
					message: `引用目标重建提交被跳过：目标草图混入外部定义变更，基线版本 ${baselineRevision}，当前版本 ${
						targetSketch.definitionRevision
					}，关联引用: ${changes.map((change) => change.referenceId).join(', ')}`,
					referenceId: firstChange?.referenceId,
					targetEntityId: firstChange?.newTargetEntityId,
				}));
				return;
			}

			if (input.allowDirtyTargetSketches !== true && targetSketch.hasUncommittedDefinitionChanges()) {
				rejectedSketchIds.push(targetSketchId);
				const firstChange = changes[0];
				diagnostics.push(createExecutionDiagnostic({
					kind: 'rebuild-failed',
					level: 'warning',
					message: `引用目标重建提交被跳过：目标草图存在未提交定义变更 ${targetSketchId}`,
					referenceId: firstChange?.referenceId,
					targetEntityId: firstChange?.newTargetEntityId,
				}));
				return;
			}

			const changedObjectIds = this.collectUnique(changes.map((change) => change.newTargetEntityId));
			const sourceObjectId = changedObjectIds[0];
			if (!sourceObjectId) {
				rejectedSketchIds.push(targetSketchId);
				return;
			}

			const replacedObjectIds = changes
				.filter((change) => (
					change.changeKind === 'replaced'
					&& change.oldTargetEntityId !== change.newTargetEntityId
				))
				.map((change) => ({
					oldObjectId: change.oldTargetEntityId,
					newObjectId: change.newTargetEntityId,
				}));
			const metadata = {
				reason: REFERENCE_TARGET_REBUILT_COMMIT_REASON,
				sourceObjectId,
				changedObjectIds,
				originReferenceIds: this.collectUnique([
					...(input.visitedReferenceIds ?? []),
					...batchReferenceIds,
				]),
				propagationId,
				replacedObjectIds: replacedObjectIds.length > 0 ? replacedObjectIds : undefined,
			};

			sketchCommits.push({targetSketch, metadata});
			preparedSketchIds.push(targetSketchId);
		});

		return {
			sketchCommits,
			preparedSketchIds,
			rejectedSketchIds,
			diagnostics,
		};
	}

	/**
	 * 发布已经通过准备阶段的目标草图定义变化。
	 *
	 * 该阶段不再执行草图存在性、版本和 dirty 校验，保证仓储正式提交后只剩确定性事件发布。
	 *
	 * @param preparation 提交前准备结果。
	 * @returns 已发布目标草图集合。
	 */
	public commitPrepared(
		preparation: PreparedSketchEntityReferenceTargetChanges,
	): CommitSketchEntityReferenceTargetChangesResult {
		const committedSketchIds: string[] = [];
		const diagnostics: ExecutionDiagnostic[] = [];
		preparation.sketchCommits.forEach(({targetSketch, metadata}) => {
			const errors: unknown[] = [];
			try {
				targetSketch.markDefinitionDirty(metadata);
			} catch (error) {
				errors.push(error);
			}
			try {
				targetSketch.commitDefinitionChanges(metadata);
			} catch (error) {
				errors.push(error);
			}
			if (!targetSketch.hasUncommittedDefinitionChanges()) {
				committedSketchIds.push(targetSketch.persistentId);
			}
			errors.forEach((error) => {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'compensation-failed',
					level: 'warning',
					message: `引用目标定义已提交，但事件监听器执行失败: ${error instanceof Error ? error.message : String(error)}`,
					targetSketchId: targetSketch.persistentId,
				}));
			});
		});
		return {
			committedSketchIds,
			diagnostics,
		};
	}

	/**
	 * 按目标草图聚合变化。
	 *
	 * @param changes 目标实体变化。
	 * @returns 目标草图到变化列表的映射。
	 */
	private groupChangesByTargetSketch(
		changes: SketchEntityReferenceTargetChange[],
	): Map<string, SketchEntityReferenceTargetChange[]> {
		const changesByTargetSketchId = new Map<string, SketchEntityReferenceTargetChange[]>();
		changes.forEach((change) => {
			const sketchChanges = changesByTargetSketchId.get(change.targetSketchId) ?? [];
			sketchChanges.push(change);
			changesByTargetSketchId.set(change.targetSketchId, sketchChanges);
		});
		return changesByTargetSketchId;
	}

	/**
	 * 按首次出现顺序收集唯一字符串。
	 *
	 * @param values 待去重字符串。
	 * @returns 去重后的字符串列表。
	 */
	private collectUnique(values: string[]): string[] {
		return [...new Set(values)];
	}

	/**
	 * 创建一轮引用传播 ID。
	 *
	 * @returns 尽量稳定唯一的传播 ID。
	 */
	private createPropagationId(): string {
		const cryptoApi = globalThis.crypto;
		if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
			return cryptoApi.randomUUID();
		}

		return `reference-propagation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
	}
}
